import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../../services/shared/apiService';
import { useAuth } from './AuthContext';
import type { UserSettings } from '../../shared/types/src';
import type { ContentMode, Theme, MemoryMode } from '../../shared/types/src';

interface Settings {
  theme: Theme;
  contentMode: ContentMode;
  modelPreference: string;
  ethicsOverride: boolean;
  memoryMode: MemoryMode;
  hasParentalControls: boolean;
}

export interface SavedMemory {
  id: string;
  content: string;
  timestamp: string;
  tags?: string[];
}

export interface TimelineEntry {
  id:string;
  content: string;
  timestamp: string;
  tags?: string[];
}

interface SettingsContextType {
  settings: Settings;
  isLoadingSettings: boolean;
  settingsError: string | null;
  updateSettings: (newSettings: Partial<Settings>) => Promise<Partial<Settings> | undefined>;
  memoryMode: MemoryMode;
  setMemoryMode: (mode: MemoryMode) => void;
  modelPreference: string;
  setModelPreference: (model: string) => void;
  ethicsOverride: boolean;
  setEthicsOverride: (override: boolean) => void;
  savePreferences: () => Promise<void>;
  savedMemories: SavedMemory[];
  pinnedTimeline: TimelineEntry[];
  refreshData: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const defaultSettings: Settings = {
  contentMode: 'FULL',
  theme: 'light',
  modelPreference: 'claude',
  ethicsOverride: false,
  memoryMode: 'server',
  hasParentalControls: false,
};

function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [savedMemories] = useState<SavedMemory[]>([]);
  const [pinnedTimeline] = useState<TimelineEntry[]>([]);

  const { user } = useAuth();

  const setMemoryMode = (mode: MemoryMode) => {
    setSettings(prev => ({ ...prev, memoryMode: mode }));
  };

  const setModelPreference = (model: string) => {
    setSettings(prev => ({ ...prev, modelPreference: model }));
  };

  const setEthicsOverride = (override: boolean) => {
    setSettings(prev => ({ ...prev, ethicsOverride: override }));
  };

  const savePreferences = async () => {
    if (!user) return;
    await updateSettings(settings);
  };

  const loadSettings = useCallback(async () => {
    if (!user) return;
    
    setIsLoadingSettings(true);
    setSettingsError(null);
    
    try {
      const userSettings: UserSettings = await apiService.getUserSettings(user.uid);
      
      // Map the full UserSettings to the simplified Settings
      const settings: Settings = {
        theme: userSettings.theme,
        contentMode: userSettings.contentMode,
        modelPreference: userSettings.modelPreference,
        ethicsOverride: userSettings.ethicsOverride,
        memoryMode: userSettings.memoryMode,
        hasParentalControls: userSettings.hasParentalControls ?? false,
      };

      setSettings(settings);

    } catch (error) {
      // Handle 404 and other errors gracefully - use default settings
      console.warn('Failed to load settings from backend (this is normal for new users):', error);
      setSettingsError(null); // Don't show error for expected 404s
    } finally {
      setIsLoadingSettings(false);
    }
  }, [user]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const updateSettings = async (newSettings: Partial<Settings>) => {
    if (!user) return undefined;

    setIsLoadingSettings(true);
    setSettingsError(null);
    
    try {
      const updatedLocalSettings = { ...settings, ...newSettings };
      setSettings(updatedLocalSettings);

      const updatedSettings = await apiService.updateUserSettings(user.uid, newSettings);
      
      if (JSON.stringify(updatedSettings) !== JSON.stringify(updatedLocalSettings)) {
        setSettings(prevSettings => ({ ...prevSettings, ...updatedSettings }));
      }
      return updatedSettings;
    } catch (error) {
      console.error('Error updating settings:', error);
      setSettingsError(error instanceof Error ? error.message : 'Failed to update settings');
      await loadSettings();
      return undefined;
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const refreshData = useCallback(async () => {
    if (user) {
      await loadSettings();
    }
  }, [user, loadSettings]);

  return (
    <SettingsContext.Provider
      value={{
        settings,
        isLoadingSettings,
        settingsError,
        updateSettings,
        memoryMode: settings.memoryMode,
        setMemoryMode,
        modelPreference: settings.modelPreference,
        setModelPreference,
        ethicsOverride: settings.ethicsOverride,
        setEthicsOverride,
        savePreferences,
        savedMemories,
        pinnedTimeline,
        refreshData
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export { SettingsProvider, useSettings }; 